import { Badge } from "@package/subframe/components/Badge";
import { Link } from "@tanstack/react-router";

import { EmployeeAvatar } from "@/components/people/EmployeeAvatar";
import { AccountStatus } from "@/generated/model/accountStatus";
import type { EmployeeDirectoryEntryDto } from "@/generated/model/employeeDirectoryEntryDto";
import {
  accountStatusLabel,
  employmentStatusLabel,
  formatProfileTimestamp,
  textOrNotRecorded,
} from "@/lib/people";
import {
  acknowledgementLabel,
  acknowledgementVariant,
  employeeName,
  employmentVariant,
} from "@/lib/people-directory";
import { staffUsername } from "@/lib/staff-username";

interface PeopleDirectoryCardsProps {
  employees: EmployeeDirectoryEntryDto[];
  showLogin: boolean;
}

/** The directory on a phone, where nine columns cannot fit. */
export function PeopleDirectoryCards({
  employees,
  showLogin,
}: PeopleDirectoryCardsProps) {
  return (
    <ul className="flex w-full flex-col gap-3" aria-label="Employee directory">
      {employees.map((employee) => {
        const name = employeeName(employee);
        const login = employee.loginAndSecurity;

        return (
          <li
            key={employee.userId}
            className="relative flex w-full flex-col gap-3 rounded-md border border-solid border-neutral-border bg-default-background p-4"
          >
            <div className="flex items-center gap-3">
              <EmployeeAvatar
                name={name}
                photoUrl={employee.photoUrl}
                size="small"
                lazy
              />
              <div className="flex min-w-0 flex-col">
                {/* Stretched over the whole card, so the card opens the profile. */}
                <Link
                  to="/people/$userId"
                  params={{ userId: employee.userId }}
                  className="text-body-bold font-body-bold text-default-font after:absolute after:inset-0"
                >
                  {name}
                </Link>
                <span className="break-all text-caption font-caption text-subtext-color">
                  {staffUsername(employee.email)}
                </span>
              </div>
            </div>
            <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-caption font-caption">
              <dt className="text-subtext-color">Role</dt>
              <dd className="text-default-font">
                {textOrNotRecorded(employee.jobTitle)}
              </dd>
              <dt className="text-subtext-color">Room</dt>
              <dd className="text-default-font">
                {textOrNotRecorded(employee.room?.name)}
              </dd>
              <dt className="text-subtext-color">Line manager</dt>
              <dd className="text-default-font">
                {textOrNotRecorded(employee.lineManager?.name)}
              </dd>
              {showLogin && login && (
                <>
                  <dt className="text-subtext-color">Last login</dt>
                  <dd className="text-default-font">
                    {formatProfileTimestamp(login.lastLoginAt)}
                  </dd>
                </>
              )}
            </dl>
            <div className="flex flex-wrap gap-2">
              {employee.employmentStatus && (
                <Badge variant={employmentVariant(employee.employmentStatus)}>
                  {employmentStatusLabel(employee.employmentStatus)}
                </Badge>
              )}
              {showLogin && login && (
                <Badge
                  variant={
                    login.accountStatus === AccountStatus.ACTIVE
                      ? "success"
                      : "neutral"
                  }
                >
                  {accountStatusLabel(login.accountStatus)}
                </Badge>
              )}
              <Badge
                variant={acknowledgementVariant(
                  employee.acknowledgements.standing,
                )}
              >
                {acknowledgementLabel(employee.acknowledgements)}
              </Badge>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
